import {
	alpha,
	Box,
	ButtonBase,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import {
	ChevronRight,
	MessageCircle,
	Plus,
	Sparkles,
	Target,
} from "lucide-react";
import type { FC } from "react";
import { ExampleGoalList } from "web_myplann/Goals/Components/ExampleGoalList";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

export const GoalsEmptyState: FC = () => {
	const theme = useTheme();
	const { palette } = theme;
	const { t } = useResource();

	return (
		<Stack>
			<Box
				sx={{
					position: "relative",
					background: theme.gradients.cta,
					px: 1.6,
					pt: "3.5rem",
					pb: 2,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						overflow: "hidden",
						pointerEvents: "none",
					}}
				>
					<Sparkles
						size={64}
						style={{
							position: "absolute",
							top: 25,
							right: 24,
							color: alpha(palette.darkmodeText.white, 0.05),
						}}
					/>
					<Target
						size={48}
						style={{
							position: "absolute",
							bottom: 16,
							left: 16,
							color: alpha(palette.darkmodeText.white, 0.05),
						}}
					/>
				</Box>

				<Stack
					alignItems="center"
					textAlign="center"
					mb={2.5}
					mt={3}
					position="relative"
				>
					<Typography variant="h2" color={palette.darkmodeText.white} mb={1}>
						{t(Resources.Goals.EmptyState.title)}
					</Typography>
					<Typography
						variant="caption"
						color={alpha(palette.darkmodeText.white, 0.7)}
						lineHeight={1.5}
						maxWidth={260}
					>
						{t(Resources.Goals.EmptyState.description)}
					</Typography>
				</Stack>

				<Stack gap={1} mt={5} position="relative">
					<ButtonBase sx={{ width: "100%", borderRadius: "12px" }}>
						<Stack
							direction="row"
							alignItems="center"
							gap={1.2}
							sx={{
								width: "100%",
								bgcolor: alpha(palette.darkmodeText.white, 0.15),
								backdropFilter: "blur(8px)",
								borderRadius: "12px",
								px: 1.6,
								py: 1.2,
							}}
						>
							<IconBox bg={palette.cta.main}>
								<Plus size={20} color={palette.darkmodeText.white} />
							</IconBox>
							<Typography
								variant="bodyMedium"
								color={palette.darkmodeText.white}
							>
								{t(Resources.Goals.EmptyState.addGoalButton)}
							</Typography>
							<ChevronRight
								size={16}
								color={alpha(palette.darkmodeText.white, 0.5)}
								style={{ marginLeft: "auto" }}
							/>
						</Stack>
					</ButtonBase>

					<ButtonBase sx={{ width: "100%", borderRadius: "12px" }}>
						<Stack
							direction="row"
							alignItems="center"
							gap={1.2}
							sx={{
								width: "100%",
								bgcolor: alpha(palette.darkmodeText.white, 0.15),
								backdropFilter: "blur(8px)",
								borderRadius: "12px",
								px: 1.6,
								py: 1.2,
							}}
						>
							<IconBox bg={palette.cta.dark}>
								<MessageCircle size={20} color={palette.darkmodeText.white} />
							</IconBox>
							<Typography
								variant="bodyMedium"
								color={palette.darkmodeText.white}
							>
								{t(Resources.Goals.EmptyState.getAdviceButton)}
							</Typography>
							<ChevronRight
								size={16}
								color={alpha(palette.darkmodeText.white, 0.5)}
								style={{ marginLeft: "auto" }}
							/>
						</Stack>
					</ButtonBase>
				</Stack>
			</Box>

			<Stack mt={0.8} bgcolor={palette.grey[50]}>
				<Typography
					variant="overline"
					color={palette.grey[500]}
					px={1.6}
					py={1}
				>
					{t(Resources.Goals.EmptyState.examplesTitle)}
				</Typography>

				<ExampleGoalList />
			</Stack>
		</Stack>
	);
};
